import _ from 'lodash';
import { MutableRefObject, useCallback, useEffect, useMemo, useState } from 'react';

import { useStateRef } from './useStateRef';

export function useNormalListScroll<T>({
  loadingMore,
  ref,
  loading,
  hasMore,
  registerDeps,
}: {
  loadingMore: () => Promise<any>;
  ref: MutableRefObject<HTMLElement | null>;
  loading: boolean;
  hasMore: boolean;
  registerDeps?: any[];
}) {
  const _continueList = useStateRef(loadingMore);
  const debounceLoad = useMemo(
    () => _.debounce(() => _continueList.current(), 200),
    [_continueList]
  );

  useEffect(() => {
    if (ref.current && hasMore) {
      const listener = () => {
        if (!ref.current) return;
        const { scrollTop, clientHeight, scrollHeight } = ref.current;
        if (scrollTop + clientHeight >= scrollHeight - 50) {
          debounceLoad();
        }
      };
      ref.current.addEventListener('scroll', listener);
      return () => {
        ref.current?.removeEventListener('scroll', listener);
      };
    }
  }, [_continueList, debounceLoad, hasMore, ref, registerDeps]);

  // 当还不能滚动的时候
  useEffect(() => {
    if (!ref.current) return;
    const scrollable = ref.current.scrollHeight > ref.current.clientHeight;
    if (!scrollable && !loading && hasMore) {
      _continueList.current();
    }
  }, [_continueList, hasMore, loading, ref]);
}

export function useNormalList<T>({
  loadingMore,
  ref,
  size: _size = 10,
}: {
  loadingMore: (arg: { page: number; size: number }) => Promise<{ data: T[]; hasMore: boolean }>;
  ref: MutableRefObject<HTMLElement | null>;
  size?: number;
}) {
  const [data, setData] = useState<T[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(0);
  const [size] = useState(_size);
  const continueList = useCallback(async () => {
    if (hasMore) {
      setLoading(true);
      try {
        const { data = [], hasMore = false } = await loadingMore({ page, size });
        setData((oldData) => [...oldData, ...data]);
        setHasMore(hasMore);
        setPage((oldPage) => oldPage + 1);
      } catch {
        //
      }
      setLoading(false);
    }
  }, [hasMore, loadingMore, page, size]);

  const _continueList = useStateRef(continueList);
  const debounceLoad = useMemo(
    () => _.debounce(() => _continueList.current(), 200),
    [_continueList]
  );

  useEffect(() => {
    if (ref.current) {
      const listener = () => {
        if (!ref.current) return;
        const { scrollTop, clientHeight, scrollHeight } = ref.current;
        if (scrollTop + clientHeight >= scrollHeight - 50) {
          debounceLoad();
        }
      };
      ref.current.addEventListener('scroll', listener);
      return () => {
        ref.current?.removeEventListener('scroll', listener);
      };
    }
  }, [continueList, debounceLoad, ref]);

  // 当还不能滚动的时候
  useEffect(() => {
    if (!ref.current) return;
    const scrollable = ref.current.scrollHeight > ref.current.clientHeight;
    if (!scrollable && !loading && hasMore) {
      continueList();
    }
  }, [continueList, hasMore, loading, ref]);

  const reset = useCallback(() => {
    setData([]);
    setHasMore(true);
    setPage(0);
  }, []);

  return {
    data,
    hasMore,
    loading,
    reset,
  };
}
